import { api, ApiError, req, type User } from "../api.ts";
import { commandId, ensurePlayer, readPlayer, sessionRecovery } from "../play/client.ts";
import { h, setMsg } from "../dom.ts";
import { t } from "../i18n/index.ts";
import { navigate } from "../router.ts";
import { setUser } from "../session.ts";
import { takeReturn } from "../returnContext.ts";

const key = "chessme.email-signin.v1";
interface Challenge { id: string; email: string; expires: number; resend: number; actor?: string; verification?: { request_id:string; code:string; actor_id?:string; command_id:string; save_games:boolean } }
function restored(): Challenge | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw || raw.length > 1024) return null;
    const value = JSON.parse(raw) as Challenge;
    if (/^[A-Za-z0-9_-]{24}$/.test(value.id) && typeof value.email === "string" && value.email.length <= 254 &&
        Number.isFinite(value.expires) && value.expires > Date.now() - (value.verification ? 7*86400_000 : 0) && value.expires <= Date.now() + 300_000 &&
        Number.isFinite(value.resend)) return value;
  } catch { /* Private storage may be unavailable; sign-in still works in memory. */ }
  return null;
}

export function emailSignIn(registrationOpen: boolean, options: { saveGames?: boolean; onSuccess?: () => void } = {}): HTMLElement {
  const el = h(`<section class="email-signin">
    <h2>${t("email.title")}</h2>
    <p class="fine">${t(registrationOpen ? "email.public" : "email.existing")}</p>
    <form class="email-request">
      <label class="field"><span>${t("email.address")}</span>
        <input name="email" type="email" autocomplete="email" maxlength="254" required /></label>
      <button class="btn primary" type="submit">${t("login.sendCode")}</button>
    </form>
    <form class="email-verify" hidden>
      <p class="fine destination"></p>
      <label class="field"><span>${t("login.codeLabel")}</span>
        <input name="code" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" minlength="6" maxlength="6" required /></label>
      <button class="btn primary" type="submit">${t("login.verifyCode")}</button>
      <button class="btn ghost resend" type="button">${t("email.resend")}</button>
      <button class="btn ghost change" type="button">${t("email.change")}</button>
    </form>
    <p class="msg" role="status" aria-live="polite"></p>
  </section>`);
  const request = el.querySelector<HTMLFormElement>(".email-request")!;
  const verify = el.querySelector<HTMLFormElement>(".email-verify")!;
  const email = request.querySelector<HTMLInputElement>("input")!;
  const code = verify.querySelector<HTMLInputElement>("input")!;
  const msg = el.querySelector<HTMLElement>(".msg")!;
  const resend = el.querySelector<HTMLButtonElement>(".resend")!;
  let challenge = restored(); let sending = false; let checking = false; let changedSession = false;
  function save() { try { if (challenge) sessionStorage.setItem(key, JSON.stringify(challenge)); else sessionStorage.removeItem(key); } catch { /* memory fallback */ } }
  function render() {
    request.hidden = challenge !== null; verify.hidden = challenge === null;
    if (challenge) {
      el.querySelector(".destination")!.textContent = t("email.sent", { email: challenge.email });
      const seconds = Math.max(0, Math.ceil((challenge.resend - Date.now()) / 1000));
      resend.disabled = sending || checking || seconds > 0 || !!challenge.verification;
      resend.textContent = seconds ? t("email.resendIn", { seconds }) : t("email.resend");
    }
    request.querySelector<HTMLButtonElement>("button")!.disabled = sending;
    verify.querySelector<HTMLButtonElement>('button[type="submit"]')!.disabled = checking || sending;
    el.querySelector<HTMLButtonElement>(".change")!.disabled = checking || sending || (!!challenge?.verification && !changedSession);
  }
  async function send() {
    if (sending || checking || (challenge && challenge.resend > Date.now())) return;
    const address = challenge?.email ?? email.value.trim();
    if (!challenge && !request.reportValidity()) return;
    sending = true; render(); setMsg(msg, t("login.sendingCode"));
    try {
      const player = await ensurePlayer();
      const issued = await api.requestEmailCode(address);
      challenge = { id: issued.request_id, email: address, actor:player.id, expires: Date.now() + issued.expires_in * 1000, resend: Date.now() + issued.resend_after * 1000 };
      save(); code.value = ""; setMsg(msg, t("email.instructions"));
    } catch (error) { setMsg(msg, error instanceof ApiError ? error.message : t("login.codeFailed"), "bad"); sessionRecovery(el,error); }
    finally { sending = false; render(); if (challenge) code.focus(); }
  }
  request.onsubmit = e => { e.preventDefault(); void send(); };
  resend.onclick = () => { void send(); };
  el.querySelector<HTMLButtonElement>(".change")!.onclick = () => { challenge = null; changedSession=false; save(); render(); email.focus(); };
  verify.onsubmit = async e => {
    e.preventDefault(); if (!challenge || checking || sending || !verify.reportValidity()) return;
    checking = true; render(); setMsg(msg, t("login.signingIn"));
    try {
      const current = await readPlayer();
      if (challenge.actor && current?.id !== challenge.actor) { changedSession=true; throw new Error("Your browser session changed. Return to the original browser, or choose Change email to request a new code here."); }
      challenge.verification ??= {request_id:challenge.id,code:code.value.trim(),actor_id:challenge.actor,command_id:commandId(),save_games:options.saveGames === true};
      // A lost success must keep the original command, code, actor and save intent.
      sessionStorage.setItem(key,JSON.stringify(challenge));
      const user = await req<User>("POST","/api/auth/email/verify",challenge.verification);
      challenge = null; save(); setUser(user); await readPlayer();
      if (options.onSuccess) options.onSuccess(); else navigate(takeReturn());
    } catch (error) {
      if (error instanceof ApiError && error.status < 500 && challenge) { delete challenge.verification; save(); }
      setMsg(msg, error instanceof Error ? error.message : t("login.codeFailed"), "bad");
    }
    finally { checking = false; render(); }
  };
  render();
  if (challenge?.verification) code.value = challenge.verification.code;
  // The mounted screen owns the cooldown timer, which ends after navigation.
  requestAnimationFrame(() => {
    if (!el.isConnected) return;
    const timer = window.setInterval(() => { if (!el.isConnected) window.clearInterval(timer); else render(); }, 1000);
  });
  return el;
}
